import { Alert, Box, Stack } from '@mui/material';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react';
import {
    useGetWidgetManifestQuery,
    useGetWidgetVersionsQuery,
    WIDGET_BASE_URL,
} from '@/api/v2/widgets';
import { WidgetManifestEntry, WidgetVersion } from '@/models/widget';
import { resolveDefaultVersion, resolveEntryUrl } from '@/utils/widgetVersions';
import { AddedWidget, SharedDesign } from '@/pages/widgetEditor/types';
import {
    buildInitialConfig,
    buildSharedDesign,
    findWidget,
    LIFT_OPERATOR_PARAM,
    resolveConfig,
} from '@/pages/widgetEditor/utils';
import { WidgetListCard } from '@/pages/widgetEditor/components/WidgetListCard';
import { WidgetDesignCard } from '@/pages/widgetEditor/components/WidgetDesignCard';
import { WidgetConfigCard } from '@/pages/widgetEditor/components/WidgetConfigCard';
import { WidgetPreviewCard } from '@/pages/widgetEditor/components/WidgetPreviewCard';
import { EmbedCodeCard } from '@/pages/widgetEditor/components/EmbedCodeCard';
import { DocumentationCard } from '@/pages/widgetEditor/components/DocumentationCard';
import { WidgetDocsDialog } from '@/pages/widgetEditor/components/WidgetDocsDialog';
import { WidgetVersionSelect } from '@/pages/widgetEditor/components/WidgetVersionSelect';

/**
 * Stable empty fallback: the preview memoizes on the widget list, so an inline
 * `?? []` would hand it a fresh array on every render.
 */
const NO_WIDGETS: WidgetManifestEntry[] = [];

/** Same reason as `NO_WIDGETS`: a stable empty list while versions load. */
const NO_VERSIONS: WidgetVersion[] = [];

export const WidgetEditorForm: FunctionComponent = () => {
    const {
        data: versionsIndex,
        isLoading: isVersionsLoading,
        isError: isVersionsError,
        refetch: refetchVersions,
    } = useGetWidgetVersionsQuery();

    // Unset until the operator picks one, so it follows the published default.
    const [selectedVersion, setSelectedVersion] = useState<string>();
    const version =
        selectedVersion ??
        (versionsIndex ? resolveDefaultVersion(versionsIndex) : undefined);

    /*
     * `currentData`, not `data`: after a version switch `data` still holds the
     * previous version's manifest, whose widgets would render against the new
     * version's entry.
     */
    const {
        currentData: manifest,
        isFetching: isManifestFetching,
        isError: isManifestError,
        refetch: refetchManifest,
    } = useGetWidgetManifestQuery(version ?? skipToken);
    const widgets = manifest?.widgets ?? NO_WIDGETS;

    const isLoading = isVersionsLoading || (!manifest && isManifestFetching);
    const isError = isVersionsError || isManifestError;
    const handleRetry = () =>
        isVersionsError ? refetchVersions() : refetchManifest();

    const entryUrl =
        manifest && version
            ? resolveEntryUrl(WIDGET_BASE_URL, version, manifest.entry)
            : undefined;

    // The page is unauthenticated, so the operator comes from the URL. It is
    // known at mount, which is why instances never need to be backfilled later.
    const [searchParams] = useSearchParams();
    const liftOperatorId = searchParams.get(LIFT_OPERATOR_PARAM) ?? undefined;

    // The widgets the user has placed, rendered row by row in the preview.
    const [addedWidgets, setAddedWidgets] = useState<AddedWidget[]>([]);
    const instanceCounter = useRef(0);

    // One design for the whole embed; each instance may override it.
    const [design, setDesign] = useState<SharedDesign>({});

    /*
     * The manifest arrives after the first render, so the design block is
     * seeded once it lands. Only missing keys are filled in: the user may
     * already have typed something by the time a refetch resolves, and
     * overwriting that would discard their work.
     */
    useEffect(() => {
        if (widgets.length === 0) return;
        const defaults = buildSharedDesign(widgets);
        setDesign((prev) => {
            const missing = Object.entries(defaults).filter(
                ([name]) => !(name in prev)
            );
            return missing.length === 0
                ? prev
                : { ...Object.fromEntries(missing), ...prev };
        });
    }, [widgets]);

    /*
     * `null` means the dialog opens at the general sections; a widget id opens
     * it with that widget expanded. One piece of state covers both entry
     * points, so they cannot disagree about what is open.
     */
    const [docsWidgetId, setDocsWidgetId] = useState<string | null>(null);
    const [docsOpen, setDocsOpen] = useState(false);

    const handleShowDocs = (widget: WidgetManifestEntry) => {
        setDocsWidgetId(widget.widget);
        setDocsOpen(true);
    };

    const handleOpenGeneralDocs = () => {
        setDocsWidgetId(null);
        setDocsOpen(true);
    };

    const handleAddWidget = (widget: WidgetManifestEntry) => {
        instanceCounter.current += 1;
        setAddedWidgets((prev) => [
            ...prev,
            {
                instanceId: `${widget.widget}-${instanceCounter.current}`,
                widgetId: widget.widget,
                config: buildInitialConfig(widget, liftOperatorId),
                designOverrides: {},
                hasDesignOverride: false,
            },
        ]);
    };

    const handleRemoveWidget = (instanceId: string) =>
        setAddedWidgets((prev) =>
            prev.filter((widget) => widget.instanceId !== instanceId)
        );

    const handleDesignChange = (name: string, value: string) =>
        setDesign((prev) => ({ ...prev, [name]: value }));

    const handleOverrideChange = (
        instanceId: string,
        name: string,
        value: string
    ) =>
        setAddedWidgets((prev) =>
            prev.map((widget) =>
                widget.instanceId === instanceId
                    ? {
                          ...widget,
                          designOverrides: {
                              ...widget.designOverrides,
                              [name]: value,
                          },
                      }
                    : widget
            )
        );

    const handleToggleOverride = (instanceId: string, enabled: boolean) =>
        setAddedWidgets((prev) =>
            prev.map((widget) =>
                widget.instanceId === instanceId
                    ? { ...widget, hasDesignOverride: enabled }
                    : widget
            )
        );

    /*
     * The merge happens once, here. The preview and the snippet both consume
     * the result, so neither has to know the override rules — and they can
     * never disagree about what the operator configured.
     */
    const resolvedWidgets = useMemo(
        () =>
            addedWidgets.flatMap((added) => {
                const entry = findWidget(widgets, added.widgetId);
                return entry
                    ? [
                          {
                              instance: added,
                              widget: entry,
                              config: resolveConfig(entry, design, added),
                          },
                      ]
                    : [];
            }),
        [addedWidgets, widgets, design]
    );

    return (
        <Stack spacing={6}>
            {!liftOperatorId && (
                <Alert severity='warning'>
                    liftOperatorId fehlt in der URL — die Vorschau für Widgets
                    mit Liftbetreiber-Bezug ist nicht verfügbar.
                </Alert>
            )}

            {/*
             * The widget list is a rail; everything else stacks vertically
             * beside it. `alignItems: flex-start` overrides the grid default of
             * `stretch` so the rail keeps its content height — which is also
             * what leaves the card room to travel inside its grid area while it
             * sticks below the app bar.
             */}
            <Box
                sx={{
                    display: 'grid',
                    alignItems: 'flex-start',
                    gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(240px, 320px) 1fr',
                    },
                    gap: 6,
                }}
            >
                <WidgetListCard
                    widgets={widgets}
                    isLoading={isLoading}
                    isError={isError}
                    onRetry={handleRetry}
                    onAddWidget={handleAddWidget}
                    onShowDocs={handleShowDocs}
                    /*
                     * Always rendered, never gated on the query: picking a
                     * version is the operator's job, so the control reports its
                     * own loading and failure rather than disappearing and
                     * leaving the editor looking like it has no versions.
                     */
                    versionSelect={
                        <WidgetVersionSelect
                            versions={versionsIndex?.versions ?? NO_VERSIONS}
                            value={version ?? ''}
                            onChange={setSelectedVersion}
                            isLoading={isVersionsLoading}
                            isError={isVersionsError}
                        />
                    }
                />

                {/*
                 * `minWidth: 0` overrides the grid item default of `auto`, which
                 * would otherwise let the embed snippet's `<pre>` push the
                 * column wider than the viewport instead of scrolling inside it.
                 */}
                <Stack spacing={6} sx={{ minWidth: 0 }}>
                    <DocumentationCard onOpen={handleOpenGeneralDocs} />

                    <WidgetDesignCard
                        widgets={widgets}
                        design={design}
                        onChange={handleDesignChange}
                    />

                    <WidgetConfigCard
                        resolvedWidgets={resolvedWidgets}
                        design={design}
                        onRemoveWidget={handleRemoveWidget}
                        onOverrideChange={handleOverrideChange}
                        onToggleOverride={handleToggleOverride}
                    />

                    <WidgetPreviewCard
                        resolvedWidgets={resolvedWidgets}
                        entryUrl={entryUrl}
                    />

                    <EmbedCodeCard
                        resolvedWidgets={resolvedWidgets}
                        entryUrl={entryUrl}
                    />
                </Stack>
            </Box>

            <WidgetDocsDialog
                open={docsOpen}
                onClose={() => setDocsOpen(false)}
                widgets={widgets}
                widgetId={docsWidgetId}
                entryUrl={entryUrl}
            />
        </Stack>
    );
};
